import { ICON_PATHS } from '@/components/ui/Icon/paths'
import { BRAND_PATHS } from '@/components/ui/Icon/brandPaths'
import { ROUTES } from '@/lib/paths'

/**
 * Where the admin's dropdowns get their options.
 *
 * Several fields in this content model are not free text at all. A project's
 * `classifications` point at the domains and categories defined in another
 * section; a service points at a project by slug; every `icon` is a key into
 * a set of drawn paths. Typed by hand, each of those is a chance to write a
 * value that resolves to nothing — and the site's response to a dangling
 * reference is to render nothing at all, which looks like a bug in the page
 * rather than a typo in the data.
 *
 * So the options are derived, never declared twice: the domain list *is* the
 * domains section, the project list *is* the projects section, and the icon
 * list *is* the set of paths that exist. Add a domain and it is assignable;
 * delete one and every project still pointing at it says so.
 *
 * ## Open and closed
 *
 * A closed vocabulary is one where a value outside it cannot mean anything —
 * an icon name with no path behind it draws nothing, ever. Those render as a
 * select.
 *
 * An open one accepts a new value: the set is a record of what has been used
 * so far, not a rule. A tech stack is open; so is a domain, because being
 * able to name one from inside the project you need it for is worth more
 * than being stopped, as long as the consequence is visible. Those render as
 * a combo box, and say so when the value matches nothing.
 */

export interface Option {
  value: string
  label: string
}

export interface Vocabulary {
  options: Option[]
  /** Whether a value outside `options` is allowed. */
  open: boolean
  /** Shown when an open field holds a value that resolves to nothing. */
  danglingWarning?: string
  /** Which section owns the canonical list, for the hint under the field. */
  manageAt?: string
  /** Draws the option's glyph beside the control. */
  preview?: 'icon' | 'brand'
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Every distinct string used under `key` anywhere in the document.
 *
 * This is what makes a tech stack behave like a vocabulary without anyone
 * maintaining one: the seventh project offers the six stacks already in use,
 * so "React" does not become "ReactJS" and "react" by the time there are ten
 * of them. Used for both `string[]` fields and plain labels.
 */
export const gather = (content: unknown, key: string): string[] => {
  const found = new Set<string>()

  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (!isRecord(node)) return

    for (const [k, value] of Object.entries(node)) {
      if (k === key) {
        if (typeof value === 'string' && value.trim()) {
          found.add(value.trim())
        } else if (Array.isArray(value)) {
          for (const item of value) {
            if (typeof item === 'string' && item.trim()) found.add(item.trim())
          }
        }
      }
      walk(value)
    }
  }

  walk(content)
  return [...found].sort((a, b) => a.localeCompare(b))
}

const asOptions = (values: string[]): Option[] =>
  values.map((value) => ({ value, label: value }))

const iconNames = Object.keys(ICON_PATHS).sort()
const brandNames = Object.keys(BRAND_PATHS).sort()

/** The domains section, however far it has been edited. */
const domainOptions = (content: Record<string, unknown>): Option[] => {
  const domains = content.domains
  if (!Array.isArray(domains)) return []
  return domains
    .filter(isRecord)
    .filter((d) => typeof d.id === 'string' && d.id)
    .map((d) => ({
      value: d.id as string,
      label: typeof d.name === 'string' && d.name ? d.name : (d.id as string),
    }))
}

/** The categories of one domain — which domain depends on the record. */
const categoryOptions = (
  content: Record<string, unknown>,
  domainId: unknown,
): Option[] => {
  const domains = content.domains
  if (!Array.isArray(domains) || typeof domainId !== 'string') return []

  const domain = domains.filter(isRecord).find((d) => d.id === domainId)
  const categories = domain?.categories
  if (!Array.isArray(categories)) return []

  return categories
    .filter(isRecord)
    .filter((c) => typeof c.id === 'string' && c.id)
    .map((c) => ({
      value: c.id as string,
      label: typeof c.name === 'string' && c.name ? c.name : (c.id as string),
    }))
}

const projectOptions = (content: Record<string, unknown>): Option[] => {
  const projects = content.projects
  if (!Array.isArray(projects)) return []
  return projects
    .filter(isRecord)
    .filter((p) => typeof p.slug === 'string' && p.slug)
    .map((p) => ({
      value: p.slug as string,
      label: typeof p.title === 'string' && p.title ? p.title : (p.slug as string),
    }))
}

/**
 * Keys whose values recur across entries, gathered from the document itself.
 *
 * Not references — nothing breaks if one is unique — so they are open and
 * carry no warning. They exist so the same thing is written the same way the
 * second time.
 */
const REPEATED_KEYS = new Set([
  'client',
  'organisation',
  'institution',
  'issuer',
  'level',
  'service',
  'year',
])

export interface VocabularyContext {
  /** The section being edited. */
  sectionKey?: string | undefined
  /** The list this record belongs to — `classifications`, `categories`, … */
  parentKey?: string | undefined
  /** The record being edited, for options that depend on a sibling field. */
  record?: Record<string, unknown> | undefined
  /** The whole live document, which is where references are resolved. */
  content: Record<string, unknown>
}

/**
 * The options for one field, or null when it is ordinary free text.
 *
 * Resolved per render rather than once when the form is built, because one
 * of these depends on a sibling: the categories on offer are the categories
 * of the domain chosen two fields up, and changing that has to change this.
 */
export const vocabularyFor = (
  key: string,
  { sectionKey, parentKey, record, content }: VocabularyContext,
): Vocabulary | null => {
  const lower = key.toLowerCase()

  if (lower === 'icon') {
    // Tools draw brand marks; everything else draws from the glyph set. A
    // name outside the set has no path behind it and renders nothing, so
    // neither list accepts invention.
    if (sectionKey === 'tools') {
      return { options: asOptions(brandNames), open: false, preview: 'brand' }
    }
    if (sectionKey === 'socialLinks') {
      // This data mixes the two sets, so both are offered.
      return {
        options: asOptions([...new Set([...iconNames, ...brandNames])].sort()),
        open: false,
      }
    }
    return { options: asOptions(iconNames), open: false, preview: 'icon' }
  }

  if (lower === 'domain') {
    return {
      options: domainOptions(content),
      open: true,
      manageAt: 'Domains & categories',
      danglingWarning:
        'No domain has this id. Add it under “Domains & categories”, or this will not show on the site.',
    }
  }

  if (lower === 'category') {
    // Two different `category` fields exist. Inside `classifications` it is
    // an id pointing into a domain; on the project itself it is a display
    // label like "EdTech Platform". Only the path tells them apart.
    if (parentKey === 'classifications') {
      return {
        options: categoryOptions(content, record?.domain),
        open: true,
        manageAt: 'Domains & categories',
        danglingWarning: record?.domain
          ? 'This domain has no such category. Add it under “Domains & categories”, or this will not show on the site.'
          : 'Choose a domain first — the categories on offer belong to it.',
      }
    }
    return { options: asOptions(gather(content, 'category')), open: true }
  }

  // A nav item pointing at a path with no route behind it is a link to a
  // 404. The routes are known, so they are offered.
  if (lower === 'path') {
    return {
      // `ROUTES.project` is a builder, not a path — a nav item cannot point
      // at "every project", so only the literal routes are offered.
      options: (Object.values(ROUTES) as unknown[])
        .filter((route) => typeof route === 'string')
        .map((route) => ({ value: route as string, label: route as string })),
      open: true,
      danglingWarning:
        'No page lives at this path, so this link will land on a 404.',
    }
  }

  // Fields that repeat across entries without being references to anything.
  // Offering what has been used is not a rule, it is spelling: one project
  // filed under "ESIGN/UIECC" and another under "ESIGN / UIECC" are two
  // clients as far as anything reading this is concerned.
  if (REPEATED_KEYS.has(lower)) {
    return { options: asOptions(gather(content, lower)), open: true }
  }

  if (lower === 'projectslug') {
    return {
      options: projectOptions(content),
      open: true,
      manageAt: 'Projects',
      danglingWarning:
        'No project has this slug, so this card will link nowhere.',
    }
  }

  return null
}

/**
 * Suggestions for a chip field — the values already used elsewhere.
 *
 * Open by definition: these are a record of what exists, offered so the same
 * thing is spelled the same way twice, not a list of what is permitted.
 */
export const suggestionsFor = (
  key: string,
  content: Record<string, unknown>,
): string[] => gather(content, key)
